/**
 * Customer logical Safety Scan: one eligible measurement window → one Scan.
 * Groups same-window revisions; does not treat superseded as extra Scans.
 * Window identity is UTC instants (A1), not min version / oldest row / report_id.
 */

export type LogicalScanSource = {
  id?: string | null;
  reportId?: string | null;
  scanId?: string | null;
  status?: string | null;
  version?: number | string | null;
  orgId?: string | null;
  siteId?: string | null;
  workflowId?: string | null;
  windowStartUtc?: string | null;
  windowEndUtc?: string | null;
  scopeStart?: string | null;
  scopeEnd?: string | null;
  signedAt?: string | null;
};

export type LogicalScan = LogicalScanSource & {
  reportId: string;
  windowStartUtc: string;
  windowEndUtc: string;
  status: 'draft' | 'finalized';
  revisionCount: number;
};

const ELIGIBLE = new Set(['draft', 'finalized', 'signed']);
const GROUPABLE = new Set(['draft', 'finalized', 'signed', 'superseded']);

function text(value: unknown): string {
  return String(value || '').trim();
}

function statusOf(row: LogicalScanSource): string {
  const raw = text(row.status).toLowerCase();
  if (raw === 'signed') return 'finalized';
  return raw;
}

function reportIdOf(row: LogicalScanSource): string {
  return text(row.reportId || row.scanId || row.id);
}

function versionOf(row: LogicalScanSource): number {
  const n = Number(row.version);
  return Number.isFinite(n) ? n : 0;
}

function withUtcZone(raw: string): string {
  if (/(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw)) return raw;
  return `${raw}Z`;
}

export function parseWindowInstantMs(value?: string | null): number | null {
  const raw = text(value);
  if (!raw) return null;
  const ms = Date.parse(withUtcZone(raw));
  return Number.isFinite(ms) ? ms : null;
}

export function logicalWindowKey(row: LogicalScanSource): string | null {
  const start = parseWindowInstantMs(row.windowStartUtc || row.scopeStart);
  const end = parseWindowInstantMs(row.windowEndUtc || row.scopeEnd);
  if (start == null || end == null) return null;
  return `${start}::${end}`;
}

function matchesScope(
  row: LogicalScanSource,
  scope?: { orgId?: string | null; siteId?: string | null; workflowId?: string | null },
): boolean {
  if (scope?.orgId && text(row.orgId) && text(row.orgId) !== text(scope.orgId)) return false;
  if (scope?.siteId && text(row.siteId) && text(row.siteId) !== text(scope.siteId)) return false;
  if (scope?.workflowId && text(row.workflowId) && text(row.workflowId) !== text(scope.workflowId)) return false;
  return true;
}

function statusRank(status: string): number {
  if (status === 'draft') return 0;
  if (status === 'finalized') return 1;
  return 9;
}

function pickAuthoritative(peers: LogicalScanSource[]): LogicalScanSource | null {
  const eligible = peers.filter((row) => ELIGIBLE.has(statusOf(row)) && reportIdOf(row));
  if (!eligible.length) return null;
  return [...eligible].sort((left, right) => {
    const rank = statusRank(statusOf(left)) - statusRank(statusOf(right));
    if (rank !== 0) return rank;
    const version = versionOf(right) - versionOf(left);
    if (version !== 0) return version;
    return reportIdOf(left).localeCompare(reportIdOf(right));
  })[0] || null;
}

function toLogicalScan(row: LogicalScanSource, revisionCount: number): LogicalScan | null {
  const reportId = reportIdOf(row);
  const start = text(row.windowStartUtc || row.scopeStart);
  const end = text(row.windowEndUtc || row.scopeEnd);
  const status = statusOf(row) === 'finalized' ? 'finalized' : 'draft';
  if (!reportId || !start || !end) return null;
  return {
    ...row,
    reportId,
    windowStartUtc: start,
    windowEndUtc: end,
    status,
    revisionCount,
  };
}

export function collectLogicalScanSources(input: {
  scanHistory?: Array<Record<string, unknown>> | null;
  activeDraftReportId?: string | null;
  latestFinalizedReportId?: string | null;
  firstEligibleScan?: Record<string, unknown> | null;
  orgId?: string | null;
  siteId?: string | null;
  workflowId?: string | null;
  draftWindow?: { start?: string | null; end?: string | null } | null;
  signedWindow?: { start?: string | null; end?: string | null } | null;
}): LogicalScanSource[] {
  const history = Array.isArray(input.scanHistory) ? input.scanHistory : [];
  const fromHistory: LogicalScanSource[] = history.map((item) => ({
    reportId: text(item.report_id || item.scan_id || item.reportId),
    scanId: text(item.scan_id),
    status: text(item.status),
    version: item.version as number | string | null,
    orgId: text(item.org_id || input.orgId) || null,
    siteId: text(item.site_id || input.siteId) || null,
    workflowId: text(item.workflow_id || input.workflowId) || null,
    windowStartUtc: text(item.window_start_utc || item.scope_start || item.scopeStart) || null,
    windowEndUtc: text(item.window_end_utc || item.scope_end || item.scopeEnd) || null,
    scopeStart: text(item.scope_start) || null,
    scopeEnd: text(item.scope_end) || null,
    signedAt: text(item.signed_at) || null,
  })).filter((row) => reportIdOf(row));
  if (fromHistory.length) return fromHistory;

  const first = input.firstEligibleScan && typeof input.firstEligibleScan === 'object'
    ? input.firstEligibleScan
    : null;
  const fallback: LogicalScanSource[] = [];
  if (first && text(first.report_id)) {
    fallback.push({
      reportId: text(first.report_id),
      status: text(first.status) || 'draft',
      version: first.version as number | string | null,
      orgId: input.orgId,
      siteId: input.siteId,
      workflowId: input.workflowId,
      windowStartUtc: text(first.window_start_utc) || null,
      windowEndUtc: text(first.window_end_utc) || null,
    });
  }
  const draftId = text(input.activeDraftReportId);
  if (draftId && !fallback.some((row) => reportIdOf(row) === draftId)) {
    fallback.push({
      reportId: draftId,
      status: 'draft',
      orgId: input.orgId,
      siteId: input.siteId,
      workflowId: input.workflowId,
      windowStartUtc: input.draftWindow?.start || null,
      windowEndUtc: input.draftWindow?.end || null,
      scopeStart: input.draftWindow?.start || null,
      scopeEnd: input.draftWindow?.end || null,
    });
  }
  const signedId = text(input.latestFinalizedReportId);
  if (signedId && !fallback.some((row) => reportIdOf(row) === signedId)) {
    fallback.push({
      reportId: signedId,
      status: 'finalized',
      orgId: input.orgId,
      siteId: input.siteId,
      workflowId: input.workflowId,
      windowStartUtc: input.signedWindow?.start || null,
      windowEndUtc: input.signedWindow?.end || null,
      scopeStart: input.signedWindow?.start || null,
      scopeEnd: input.signedWindow?.end || null,
    });
  }
  return fallback;
}

export function groupLogicalScans(
  rows: LogicalScanSource[],
  scope?: { orgId?: string | null; siteId?: string | null; workflowId?: string | null },
): LogicalScan[] {
  const buckets = new Map<string, LogicalScanSource[]>();
  for (const row of rows) {
    if (!reportIdOf(row)) continue;
    if (!GROUPABLE.has(statusOf(row))) continue;
    if (!matchesScope(row, scope)) continue;
    const key = logicalWindowKey(row);
    if (!key) continue;
    const list = buckets.get(key);
    if (list) list.push(row);
    else buckets.set(key, [row]);
  }
  const grouped: LogicalScan[] = [];
  for (const peers of Array.from(buckets.values())) {
    const active = pickAuthoritative(peers);
    if (!active) continue;
    const logical = toLogicalScan(active, peers.length);
    if (logical) grouped.push(logical);
  }
  grouped.sort((left, right) => {
    const start = (parseWindowInstantMs(left.windowStartUtc) || 0) - (parseWindowInstantMs(right.windowStartUtc) || 0);
    if (start !== 0) return start;
    const end = (parseWindowInstantMs(left.windowEndUtc) || 0) - (parseWindowInstantMs(right.windowEndUtc) || 0);
    if (end !== 0) return end;
    return left.reportId.localeCompare(right.reportId);
  });
  return grouped;
}

export function selectFirstEligibleLogicalScan(
  rows: LogicalScanSource[],
  scope?: { orgId?: string | null; siteId?: string | null; workflowId?: string | null },
): LogicalScan | null {
  return groupLogicalScans(rows, scope)[0] || null;
}
